import { Box, CircularProgress, Typography } from "@mui/material";
import { PopoutContainer } from "components/common/PopoutContainer";
import { PanelErrorBoundary } from "components/common/error/PanelErrorBoundary";
import { ScaleWeightsChart } from "components/identifyEventsTool/tool/ScaleWeightsChart";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { EventCreate } from "src/store/api/generatedApi";
import { useMassData } from "./contexts/MassDataContext";

type ScaleWeightsSidebarPanelProps = Readonly<{
  videoStart: Dayjs;
  massEvents: EventCreate[];
}>;

const ScaleWeightsSidebarPanel = ({
  videoStart,
  massEvents,
}: ScaleWeightsSidebarPanelProps) => {
  const { t } = useTenantTranslation();
  const { data, isLoading, isError } = useMassData();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 120,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 120,
          px: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t("unableToLoadScaleWeights")}
        </Typography>
      </Box>
    );
  }

  if (!data || data.series.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <ScaleWeightsChart
        data={data}
        videoStart={videoStart}
        candidateEvents={massEvents}
      />
    </Box>
  );
};

type PoppableScaleWeightsPanelProps = Readonly<{
  videoStart: Dayjs;
  massEvents: EventCreate[];
  errorBoundaryPanel: string;
}>;

export const PoppableScaleWeightsPanel = ({
  videoStart,
  massEvents,
  errorBoundaryPanel,
}: PoppableScaleWeightsPanelProps) => {
  const { t } = useTenantTranslation();
  return (
    <PopoutContainer
      title={t("scaleWeights")}
      poppedOutMessage={t("scaleWeightsPoppedOut")}
      popoutWidth={400}
    >
      <PanelErrorBoundary panel={errorBoundaryPanel}>
        <ScaleWeightsSidebarPanel
          videoStart={videoStart}
          massEvents={massEvents}
        />
      </PanelErrorBoundary>
    </PopoutContainer>
  );
};
